import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import AuthLayout, { Field } from "./AuthLayout";
import Modal from "../components/common/Modal";
import * as authService from "../services/auth";
import { formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const COUNTRIES = ["India", "Brazil", "Pakistan", "Bangladesh", "Nigeria", "South Africa", "Mexico", "Indonesia", "Philippines", "Egypt", "United Kingdom", "United States", "Other"];

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", country: "India", trader_id: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { applyAuth } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{8}$/.test(form.trader_id)) return toast.error("Trader ID must be exactly 8 digits.");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const data = await authService.register({
  name: form.name,
  email: form.email,
  password: form.password,
  country: form.country,
  trader_id: form.trader_id,
  affiliate_source:
    new URLSearchParams(window.location.search).get("ref") || "direct",
});

applyAuth(data);

if (data.verification?.status === "verified") {
  toast.success("Trader ID verified successfully.");
  navigate("/home");
} else if (data.verification?.status === "pending") {
  toast.error(
    data.verification.message ||
    "Your Trader ID is verified, but a minimum $50 deposit is required."
  );
  navigate("/home");
} else {
  toast.error("Verification could not be completed.");
}
    } catch (err) {
  const detail = err?.response?.data?.detail;

  toast.error(
    formatApiErrorDetail(detail) ||
    err?.message ||
    "Something went wrong. Please try again."
  );
} finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthLayout eyebrow="Register" title="Create account"
        footer={<>Already a member? <Link to="/login" className="text-white hover:underline" data-testid="signup-to-login">Sign in</Link></>}>
        <form onSubmit={submit} data-testid="signup-form">
          <div className="mb-5">
            <a
              href="https://broker-qx.pro/sign-up/?lid=2157074"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center justify-center w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 transition-all duration-200 hover:border-white/30 hover:bg-white/[0.07] hover:-translate-y-0.5"
              data-testid="signup-quotex-cta"
            >
              <div className="flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-neutral-200 group-hover:text-white">
                <ArrowUpRight size={14} className="text-[#8fd6b4] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                <span>Open Quotex Account</span>
              </div>
              <span className="mt-0.5 font-mono text-[10px] text-neutral-500 transition-colors group-hover:text-neutral-400">
                Open Quotex via provided link
              </span>
            </a>
          </div>

          <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Rahul Sharma" testid="signup-name" required />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@email.com" testid="signup-email" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="••••••••" testid="signup-password" required />
            <Field label="Confirm" type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} placeholder="••••••••" testid="signup-confirm" required />
          </div>

          <label className="mb-4 block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">Country</span>
            <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="signup-country"
              className="w-full rounded-xl border border-white/10 bg-[#0c0c0e] px-4 py-3 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60">
              {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>

          <Field label="Trader ID (8 digits)" value={form.trader_id}
            onChange={(v) => setForm({ ...form, trader_id: v.replace(/\D/g, "").slice(0, 8) })}
            placeholder="e.g. 84726193" testid="signup-trader-id" inputMode="numeric"
            hint="Your 8-digit Quotex Trader ID." />

          <button type="submit" disabled={loading} className="btn-silver sweep flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-widest transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="signup-submit-btn">
            {loading ? <Loader2 size={15} className="animate-spin" /> : "Create Account"}
          </button>
        </form>
      </AuthLayout>
    </>
  );
}
